/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Label, Button, Alert,
} from 'reactstrap';
import {
  Formik, Field, FieldArray, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { post } from '../../helpers/api_helper';

function PutawayAreaForm(props) {
  const { onSuccess, entity, t } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const lotMultilinePattern = /^(?:\^?[A-Z0-9]+)(?:\r?\n\^?[A-Z0-9]+)*$/;
  const stockIdMultilinePattern = /^(?:[A-Z0-9]{1,8})(?:\r?\n[A-Z0-9]{1,8})*$/;

  const validateLocationQuery = (value) => {
    const lines = String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      return false;
    }

    return lines.every((line) => {
      const segments = line.split('.').map((segment) => segment.trim()).filter(Boolean);

      if (segments.length < 3) {
        return false;
      }

      return segments.every((segment) => {
        if (segment === '*') {
          return true;
        }

        return segment.split(',').every((part) => {
          const token = part.trim();

          if (/^\d+$/.test(token) || /^[A-Z]+$/i.test(token)) {
            return true;
          }

          if (/^\d+-\d+$/.test(token)) {
            return true;
          }

          return /^[A-Z]-[A-Z]$/i.test(token);
        });
      });
    });
  };

  const ValidationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .max(45, 'Name is too long (max 45 characters)')
      .trim(),

    location_query: Yup.string()
      .required('Location Query is required')
      .test(
        'location-query-format',
        'Each line must look like: 10.A-Z.1-5.* or 11.AA,BB.5.A. Use commas for multi-letter values instead of ranges.',
        validateLocationQuery,
      ),
    lot_query: Yup.string()
      .matches(lotMultilinePattern, 'Invalid lot query')
      .nullable(),
    stock_id_query: Yup.string()
      .matches(stockIdMultilinePattern, 'Each stock ID must be 1–8 uppercase letters or numbers')
      .nullable(),
    priority: Yup.number()
      .required('Priority is required')
      .min(0, 'Priority must be at least 0')
      .max(255, 'Priority must be at most 255'),
  });

  const makePutawayArea = async (values) => {
    try {
      setLoading(true);
      const result = await post('/api/warehouse/putawayarea', values);
      onSuccess(result);
    } catch (error) {
      toastr.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const jsx = (
    <Formik
      enableReinitialize
      initialValues={entity || {
        name: '', priority: 128, location_query: '', lot_query: '', stock_id_query: '',
      }}
      validationSchema={ValidationSchema}
      onSubmit={(values, actions) => {
        makePutawayArea(values);
      }}
    >
      {({
        touched, setFieldValue, errors, values,
      }) => (
        <Form autoComplete="off" id="PutawayAreaForm">
          <div className="row mb-4">
            <Label
              for="name"
              className="col-sm-3 col-form-label mt-3"
            >
              Name
            </Label>
            <Col sm={9} className="mt-3">
              <Field
                type="text"
                className={`form-control ${(errors.name && touched.name ? 'is-invalid' : '')}`}
                id="name"
                name="name"
              />
              <ErrorMessage
                name="name"
                component="div"
                className="invalid-feedback"
              />
              <div className="help-block">
                <small>{t('Name of the putaway area (just for searching and reference)')}</small>
              </div>
            </Col>

            <Label
              for="priority"
              className="col-sm-3 col-form-label mt-3"
            >
              Priority
            </Label>
            <Col sm={9} className="mt-3">
              <Row>
                <Col lg="6">
                  <Field
                    type="number"
                    className={`form-control ${(errors.priority && touched.priority ? 'is-invalid' : '')}`}
                    id="priority"
                    name="priority"
                    min="0"
                    max="255"
                    step="1"
                  />
                </Col>
              </Row>
              <ErrorMessage
                name="priority"
                component="div"
                className="invalid-feedback"
              />
              <div className="help-block">
                <small>{t('Priority of the putaway area (0 = least, 255 = highest)')}</small>
              </div>
            </Col>

            <Label
              for="location_query"
              className="col-sm-3 col-form-label mt-3"
            >
              Location Query
            </Label>
            <Col sm={9} className="mt-3">
              <Field
                type="text"
                as="textarea"
                rows="3"
                className={`form-control ${(errors.location_query && touched.location_query ? 'is-invalid' : '')}`}
                id="location_query"
                name="location_query"
              />
              <ErrorMessage
                name="location_query"
                component="div"
                className="text-danger"
              />
              <div className="help-block">
                <Row>
                  <Col sm="8">
                    <small>{t('Use * as a wildcard for any variation.')}</small>
                    <br />
                    <small>{t('Use - for ranges. Letter ranges only work for single letters like A-Z.')}</small>
                    <br />
                    <small>{t('Use , to list multiple items such as AA,BB.')}</small>
                  </Col>
                  <Col sm="4">
                    <small><strong>{t('Examples')}</strong></small>
                    <br />
                    <small><strong>{t('10.A-Z.1-5.*')}</strong></small>
                    <br />
                    <small><strong>{t('11.B,P.5.A')}</strong></small>
                    <br />
                    <small><strong>{t('12.AA,BB.1.*')}</strong></small>
                  </Col>
                </Row>
              </div>
            </Col>

            <Label
              for="lot_query"
              className="col-sm-3 col-form-label mt-3"
            >
              Lot Query
            </Label>
            <Col sm={9} className="mt-3">
              <Field
                type="text"
                as="textarea"
                rows="3"
                className={`form-control ${(errors.lot_query && touched.lot_query ? 'is-invalid' : '')}`}
                id="lot_query"
                name="lot_query"
              />
              <ErrorMessage
                name="lot_query"
                component="div"
                className="invalid-feedback"
              />
              <div className="help-block">
                <Row>
                  <Col sm="8">
                    <small>{t('List lots separated by new lines.')}</small>
                  </Col>
                  <Col sm="4">
                    <small><strong>{t('Example: A123')}</strong></small>
                  </Col>
                </Row>
              </div>
            </Col>

            <Label
              for="stock_id_query"
              className="col-sm-3 col-form-label mt-3"
            >
              Stock ID Query
            </Label>
            <Col sm={9} className="mt-3">
              <Field
                type="text"
                as="textarea"
                rows="3"
                className={`form-control ${(errors.stock_id_query && touched.stock_id_query ? 'is-invalid' : '')}`}
                id="stock_id_query"
                name="stock_id_query"
              />
              <ErrorMessage
                name="stock_id_query"
                component="div"
                className="invalid-feedback"
              />
              <div className="help-block">
                <small>{t('List stock_ids separated by new lines.')}</small>
              </div>
            </Col>
          </div>
        </Form>
      )}
    </Formik>
  );

  return jsx;
}

PutawayAreaForm.defaultProps = {
  t: () => { },
  onSuccess: () => { },
  entity: {},
};

PutawayAreaForm.propTypes = {
  onSuccess: PropTypes.func,
  t: PropTypes.any,
  entity: PropTypes.object,
};

export default connect(() => ({

}), {})(withTranslation()(PutawayAreaForm));
